import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Stack,
  TextField,
  MenuItem,
  Grid,
  TablePagination,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pl';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import {
  useGetAllCashDocumentsFilteredQuery,
  useLazyGetCashDocumentPdfQuery,
} from '../../Service/extendedCashDocumentsApi';
import { useDeleteCashDocumentMutation } from '../../Service/cashDocumentsApi';
import {
  formatAmount,
  getTypeLabel,
  getTypeColor,
  CashDocumentType,
} from '../../Types/cashDocumentTypes';
import type { CashDocumentsListProps, CashDocumentsListFilters } from './CashDocumentsList.types';
import { useNavigate } from 'react-router-dom';
import { getCashDocumentsErrorMessage } from '../../Utils/cashDocumentsErrorMapping';
import { downloadCashDocumentPdf } from '../../Utils/cashDocumentPdfUtils';

const CashDocumentsList: React.FC<CashDocumentsListProps> = ({
  filters: initialFilters,
  showFilters = true,
  compact = false,
}) => {
  const navigate = useNavigate();

  // Local filter state
  const [filters, setFilters] = useState<CashDocumentsListFilters>(initialFilters || {});
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Prepare API filter params
  const filterParams = useMemo(
    () => ({
      type: filters.type,
      reservationId: filters.reservationId,
      invoiceId: filters.invoiceId,
      personName: filters.personName?.trim() || undefined,
      documentNumber: filters.documentNumber?.trim() || undefined,
      dateFrom: dateFrom?.format('YYYY-MM-DD'),
      dateTo: dateTo?.format('YYYY-MM-DD'),
    }),
    [filters, dateFrom, dateTo]
  );

  const apiParams = useMemo(
    () => ({
      ...filterParams,
      pageNumber,
      pageSize,
    }),
    [filterParams, pageNumber, pageSize]
  );

  // Fetch documents
  const {
    data: pagedDocuments,
    isLoading,
    error,
    refetch,
  } = useGetAllCashDocumentsFilteredQuery(apiParams);

  const documents = pagedDocuments?.items ?? [];
  const totalCount = pagedDocuments?.totalCount ?? 0;

  useEffect(() => {
    setPageNumber(1);
  }, [filterParams]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, pageSize, totalCount]);

  // Delete mutation
  const [deleteCashDocument, { isLoading: isDeleting }] = useDeleteCashDocumentMutation();
  const [triggerGetCashDocumentPdf] = useLazyGetCashDocumentPdfQuery();

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const handleDelete = async (id: number) => {
    if (deleteConfirmId !== id) {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000); // Reset after 3s
      return;
    }

    try {
      await deleteCashDocument(id).unwrap();
      setDeleteConfirmId(null);
      refetch();
    } catch (err) {
      console.error('Delete failed:', err);
      alert(getCashDocumentsErrorMessage(err, 'Nie udało się usunąć dokumentu. Spróbuj ponownie.'));
    }
  };

  const handleEdit = (id: number) => {
    navigate(`/cash-documents/edit/${id}`);
  };

  const handleDownloadPdf = async (id: number, documentNumber: string) => {
    try {
      await downloadCashDocumentPdf(triggerGetCashDocumentPdf, id, `${documentNumber}.pdf`);
    } catch (err) {
      console.error('PDF download failed:', err);
      alert(getCashDocumentsErrorMessage(err, 'Nie udało się pobrać PDF. Spróbuj ponownie.'));
    }
  };

  const handleClearFilters = () => {
    setFilters({});
    setDateFrom(null);
    setDateTo(null);
    setPageNumber(1);
  };

  const hasActiveFilters = useMemo(() => {
    return !!(
      filterParams.type ||
      filterParams.reservationId ||
      filterParams.invoiceId ||
      filterParams.personName ||
      filterParams.documentNumber ||
      filterParams.dateFrom ||
      filterParams.dateTo
    );
  }, [filterParams]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
      <Box>
        {/* Filters Section */}
        {showFilters && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: showFilterPanel ? 2 : 0,
              }}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon />
                Filtry
                {hasActiveFilters && (
                  <Chip
                    label={`Aktywne: ${Object.values(filterParams).filter(Boolean).length}`}
                    size="small"
                    color="primary"
                  />
                )}
              </Typography>
              <Stack direction="row" spacing={1}>
                {hasActiveFilters && (
                  <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters}>
                    Wyczyść
                  </Button>
                )}
                <Button
                  size="small"
                  variant={showFilterPanel ? 'contained' : 'outlined'}
                  onClick={() => setShowFilterPanel(!showFilterPanel)}
                >
                  {showFilterPanel ? 'Ukryj' : 'Pokaż'}
                </Button>
              </Stack>
            </Box>

            {showFilterPanel && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Typ dokumentu"
                    value={filters.type || ''}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        type: (e.target.value as CashDocumentType) || undefined,
                      })
                    }
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    <MenuItem value="KP">KP (Kasa Przyjmie)</MenuItem>
                    <MenuItem value="KW">KW (Kasa Wypłaci)</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                  <DatePicker
                    label="Data od"
                    value={dateFrom}
                    onChange={(newValue) => setDateFrom(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                  <DatePicker
                    label="Data do"
                    value={dateTo}
                    onChange={(newValue) => setDateTo(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="ID Rezerwacji"
                    value={filters.reservationId || ''}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        reservationId: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                  <TextField
                    fullWidth
                    size="small"
                    label="Numer KP/KW"
                    value={filters.documentNumber || ''}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        documentNumber: e.target.value || undefined,
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }} >
                  <TextField
                    fullWidth
                    size="small"
                    label="Osoba"
                    value={filters.personName || ''}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        personName: e.target.value || undefined,
                      })
                    }
                  />
                </Grid>
              </Grid>
            )}
          </Paper>
        )}

        {/* Table Section */}
        <TableContainer component={Paper}>
          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {getCashDocumentsErrorMessage(
                error,
                'Błąd pobierania dokumentów kasowych. Spróbuj ponownie.'
              )}
            </Alert>
          )}

          {/* Empty State */}
          {!isLoading && !error && documents.length === 0 && (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <ReceiptIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Brak dokumentów kasowych
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {hasActiveFilters ? 'Spróbuj zmienić filtry' : 'Utwórz pierwszy dokument kasowy'}
              </Typography>
            </Box>
          )}

          {/* Table */}
          {!isLoading && !error && documents.length > 0 && (
            <Table size={compact ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Numer</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Osoba</TableCell>
                  <TableCell align="right">Kwota</TableCell>
                  <TableCell>Waluta</TableCell>
                  <TableCell>Powiązanie</TableCell>
                  <TableCell align="center">Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow
                    key={doc.id}
                    hover
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      opacity: deleteConfirmId === doc.id ? 0.6 : 1,
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {doc.documentNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTypeLabel(doc.type)}
                        color={getTypeColor(doc.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {doc.documentDate ? dayjs(doc.documentDate).format('DD.MM.YYYY') : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {doc.personName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        color={doc.type === CashDocumentType.KP ? 'success.main' : 'error.main'}
                      >
                        {formatAmount(doc.amount, doc.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={doc.currency} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {doc.reservationId && (
                          <Chip
                            label={`Rez. #${doc.reservationId}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={() => navigate(`/reservations/update/${doc.reservationId}`)}
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                        {doc.invoiceId && (
                          <Chip
                            label={`FV #${doc.invoiceId}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => navigate(`/invoices/update/${doc.invoiceId}`)}
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                        {!doc.reservationId && !doc.invoiceId && (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Pobierz PDF">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownloadPdf(doc.id, doc.documentNumber)}
                          >
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edytuj">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(doc.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            deleteConfirmId === doc.id ? 'Kliknij ponownie aby potwierdzić' : 'Usuń'
                          }
                        >
                          <IconButton
                            size="small"
                            color={deleteConfirmId === doc.id ? 'error' : 'default'}
                            onClick={() => handleDelete(doc.id)}
                            disabled={isDeleting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={Math.max(pageNumber - 1, 0)}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => setPageNumber(newPage + 1)}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPageNumber(1);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />

        {/* Summary */}
        {!isLoading && totalCount > 0 && (
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Wyświetlono: {documents.length} z {totalCount}
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default CashDocumentsList;
